import { useEffect, useRef, useState } from 'react';
import { Pause, Play, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { EntryWidgetProps } from './types';

/*
 * Duration stopwatch — captures elapsed seconds. valueNumeric = total
 * seconds. The server rejects valueJson for duration targets.
 *
 * UX: start / pause / reset. Manual entry still permitted in the input,
 * so clinicians can key in a known duration without live timing.
 *
 * Component-local timer state (running + rafId + accumulatedMs) is kept
 * in a ref + useState so pause/resume can add up partial runs without
 * losing precision. On unmount we cancel the active interval.
 */
export function DurationStopwatch({ value, onChange, disabled }: EntryWidgetProps) {
    const [running, setRunning] = useState(false);
    const startedAt = useRef<number | null>(null);
    const intervalId = useRef<number | null>(null);
    const accumulatedMs = useRef<number>((value.valueNumeric ?? 0) * 1000);
    const [displayMs, setDisplayMs] = useState<number>(accumulatedMs.current);

    useEffect(() => {
        if (!running) return;
        startedAt.current = performance.now();
        intervalId.current = window.setInterval(() => {
            if (startedAt.current !== null) {
                setDisplayMs(accumulatedMs.current + (performance.now() - startedAt.current));
            }
        }, 100);
        return () => {
            if (intervalId.current !== null) {
                window.clearInterval(intervalId.current);
                intervalId.current = null;
            }
        };
    }, [running]);

    const commitSeconds = (seconds: number) => {
        const safe = Math.max(0, Math.round(seconds));
        accumulatedMs.current = safe * 1000;
        setDisplayMs(accumulatedMs.current);
        onChange({ ...value, valueNumeric: safe, valueJson: null, dirty: true });
    };

    const toggle = () => {
        if (disabled) return;
        if (running) {
            if (startedAt.current !== null) {
                accumulatedMs.current += performance.now() - startedAt.current;
                startedAt.current = null;
            }
            setRunning(false);
            commitSeconds(accumulatedMs.current / 1000);
        } else {
            setRunning(true);
        }
    };

    const reset = () => {
        setRunning(false);
        startedAt.current = null;
        accumulatedMs.current = 0;
        setDisplayMs(0);
        onChange({ ...value, valueNumeric: 0, valueJson: null, dirty: true });
    };

    const totalSec = Math.floor(displayMs / 1000);
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;

    return (
        <div className="flex items-center gap-2">
            <div className="font-mono text-lg tabular-nums px-3 py-1 bg-background border border-border rounded-md min-w-[4.5rem] text-center">
                {mm}:{ss.toString().padStart(2, '0')}
            </div>
            <Button
                type="button"
                variant={running ? 'ghost' : 'primary'}
                size="sm"
                onClick={toggle}
                disabled={disabled}
            >
                {running ? (<><Pause className="w-3.5 h-3.5" /> Pause</>) : (<><Play className="w-3.5 h-3.5" /> Start</>)}
            </Button>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={reset}
                disabled={disabled || displayMs === 0}
                aria-label="Reset stopwatch"
            >
                <RotateCcw className="w-3.5 h-3.5" />
            </Button>
            <span className="text-xs text-text-subtle ml-1">or key seconds:</span>
            <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                value={value.valueNumeric ?? ''}
                onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                        onChange({ ...value, valueNumeric: null, valueJson: null, dirty: true });
                        return;
                    }
                    const n = Number(raw);
                    if (Number.isFinite(n)) commitSeconds(n);
                }}
                disabled={disabled || running}
                className="w-20 px-2 py-1 text-sm text-right bg-background border border-border rounded-md focus:border-accent focus:outline-none disabled:opacity-50"
            />
        </div>
    );
}
